<template>
  <div class="shop-order-page">
    <nav_list :index="4"></nav_list>

    <div class="order-wrap">
      <div v-if="loading" class="state-card">
        <i class="el-icon-loading"></i>
        <span>预订信息加载中...</span>
      </div>

      <div v-else-if="!requestData" class="state-card state-card--empty">
        <div class="state-main">
          <div class="state-title">没有可用的预订信息</div>
          <div class="state-desc">请先从商品详情页选择套餐和出行配置，再进入订单填写页。</div>
          <el-button type="primary" @click="jump('/shop')">返回商城</el-button>
        </div>
      </div>

      <template v-else>
        <div class="breadcrumb-row">
          <el-page-header @back="goBack" content="填写预订信息"></el-page-header>
        </div>

        <div class="order-layout">
          <div class="order-main">
            <section class="order-card hero-card">
              <div class="hero-head">
                <div>
                  <div class="hero-badge">预订确认</div>
                  <h1 class="hero-title">{{ productTitle }}</h1>
                  <div class="hero-subtitle">{{ packageTitle }}</div>
                </div>
                <div class="hero-price">
                  <span class="price-prefix">¥</span>
                  <span class="price-value">{{ formatMoney(orderTotalPrice) }}</span>
                </div>
              </div>

              <div class="hero-meta">
                <div class="meta-chip" v-if="shouldShowServiceDate()">
                  <span class="meta-label">服务日期</span>
                  <span class="meta-value">{{ booking.s_date || '-' }}</span>
                </div>
                <div class="meta-chip">
                  <span class="meta-label">购买份数</span>
                  <span class="meta-value">{{ totalQuantity }} {{ orderUnit || '份' }}</span>
                </div>
                <div class="meta-chip" v-if="requestData.event_time">
                  <span class="meta-label">出行时段</span>
                  <span class="meta-value">{{ requestData.event_time }}</span>
                </div>
              </div>

              <div v-if="selectedSpecTexts.length" class="selected-specs">
                <div class="inline-title">已选规格</div>
                <div class="spec-list">
                  <span v-for="item in selectedSpecTexts" :key="item" class="spec-tag">{{ item }}</span>
                </div>
              </div>
            </section>

            <section v-if="refundPolicyLines.length" class="order-card">
              <div class="section-title">取消政策</div>
              <ul class="policy-list">
                <li v-for="(item, index) in refundPolicyLines" :key="'policy-' + index">
                  {{ item }}
                </li>
              </ul>
            </section>

            <section class="order-card">
              <div class="section-title">买家信息</div>
              <div class="form-grid">
                <div v-if="showBuyerField('first_name')" class="form-item">
                  <label>买家名<span class="required">*</span></label>
                  <el-input v-model.trim="booking.buyer_first_name" placeholder="请输入买家名"></el-input>
                </div>
                <div v-if="showBuyerField('last_name')" class="form-item">
                  <label>买家姓<span class="required">*</span></label>
                  <el-input v-model.trim="booking.buyer_last_name" placeholder="请输入买家姓"></el-input>
                </div>
                <div v-if="showBuyerField('tel')" class="form-item">
                  <label>联系电话<span class="required">*</span></label>
                  <el-input v-model.trim="booking.buyer_tel_number" placeholder="请输入电话号码"></el-input>
                </div>
                <div v-if="showBuyerField('email')" class="form-item">
                  <label>邮箱<span class="required">*</span></label>
                  <el-input v-model.trim="booking.buyer_Email" placeholder="请输入邮箱"></el-input>
                </div>
              </div>
            </section>

            <section v-if="showGuideLang" class="order-card">
              <div class="section-title">导游语言</div>
              <div class="form-grid">
                <div class="form-item">
                  <label>导游语言<span class="required">*</span></label>
                  <el-select v-model="booking.guide_lang" filterable placeholder="请选择导游语言">
                    <el-option
                      v-for="item in getFieldOptions(bookingField.guide_lang, 'guide_lang')"
                      :key="'lang-' + getOptionValue(item)"
                      :label="getOptionLabel(item)"
                      :value="getOptionValue(item)"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section
              v-for="(customer, index) in travelerCustomers"
              :key="'traveler-' + index"
              class="order-card"
            >
              <div class="section-title">{{ getCustomerTitle(customer, index, 'traveler') }}</div>
              <div class="form-grid">
                <template v-for="field in getVisibleCustomFields(customer.cus_type)">
                  <div
                    v-if="getFieldMode(field.config, field.key) !== 'country_cities'"
                    :key="'cus-' + index + '-' + field.key"
                    class="form-item"
                    :class="{ 'form-item--full': isWideField(field.key) }"
                  >
                    <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>

                    <el-input
                      v-if="getFieldMode(field.config, field.key) === 'text'"
                      v-model.trim="customer[field.key]"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-input
                      v-else-if="getFieldMode(field.config, field.key) === 'number'"
                      v-model.trim="customer[field.key]"
                      type="number"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-input
                      v-else-if="getFieldMode(field.config, field.key) === 'textarea'"
                      v-model.trim="customer[field.key]"
                      type="textarea"
                      :rows="3"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-date-picker
                      v-else-if="getFieldMode(field.config, field.key) === 'date'"
                      v-model="customer[field.key]"
                      type="date"
                      value-format="yyyy-MM-dd"
                      placeholder="请选择日期"
                      style="width: 100%;"
                    ></el-date-picker>

                    <el-time-picker
                      v-else-if="getFieldMode(field.config, field.key) === 'time'"
                      v-model="customer[field.key]"
                      value-format="HH:mm:ss"
                      placeholder="请选择时间"
                      style="width: 100%;"
                    ></el-time-picker>

                    <el-switch
                      v-else-if="getFieldMode(field.config, field.key) === 'boolean'"
                      v-model="customer[field.key]"
                      :active-value="true"
                      :inactive-value="false"
                    ></el-switch>

                    <el-select
                      v-else
                      v-model="customer[field.key]"
                      filterable
                      placeholder="请选择"
                    >
                      <el-option
                        v-for="item in getFieldOptions(field.config, field.key)"
                        :key="'cus-opt-' + index + '-' + field.key + '-' + getOptionValue(item)"
                        :label="getOptionLabel(item)"
                        :value="getOptionValue(item)"
                      />
                    </el-select>
                  </div>

                  <div
                    v-else
                    :key="'cus-country-' + index + '-' + field.key"
                    class="form-item form-item--full"
                  >
                    <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>
                    <div class="inline-field">
                      <el-select
                        :value="getCountryParent(customer, getCustomerIndex(customer))"
                        filterable
                        placeholder="选择国家/地区"
                        @input="setCountryParent(customer, getCustomerIndex(customer), $event)"
                      >
                        <el-option
                          v-for="item in getFieldOptions(field.config, field.key)"
                          :key="'country-parent-' + index + '-' + item.id"
                          :label="getOptionLabel(item)"
                          :value="String(item.id)"
                        />
                      </el-select>
                      <el-select
                        v-model="customer[field.key]"
                        filterable
                        placeholder="选择城市"
                      >
                        <el-option
                          v-for="city in getCountryCities(customer, getCustomerIndex(customer), field.config)"
                          :key="'country-city-' + index + '-' + city.city_code"
                          :label="city.name"
                          :value="city.city_code"
                        />
                      </el-select>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <section
              v-for="(customer, index) in supportCustomers"
              :key="'support-' + index"
              class="order-card"
            >
              <div class="section-title">{{ getCustomerTitle(customer, index, 'support') }}</div>
              <div class="form-grid">
                <template v-for="field in getVisibleCustomFields(customer.cus_type)">
                  <div
                    v-if="getFieldMode(field.config, field.key) !== 'country_cities'"
                    :key="'support-field-' + index + '-' + field.key"
                    class="form-item"
                    :class="{ 'form-item--full': isWideField(field.key) }"
                  >
                    <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>

                    <el-input
                      v-if="getFieldMode(field.config, field.key) === 'text'"
                      v-model.trim="customer[field.key]"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-input
                      v-else-if="getFieldMode(field.config, field.key) === 'number'"
                      v-model.trim="customer[field.key]"
                      type="number"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-input
                      v-else-if="getFieldMode(field.config, field.key) === 'textarea'"
                      v-model.trim="customer[field.key]"
                      type="textarea"
                      :rows="3"
                      :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                    ></el-input>

                    <el-date-picker
                      v-else-if="getFieldMode(field.config, field.key) === 'date'"
                      v-model="customer[field.key]"
                      type="date"
                      value-format="yyyy-MM-dd"
                      placeholder="请选择日期"
                      style="width: 100%;"
                    ></el-date-picker>

                    <el-time-picker
                      v-else-if="getFieldMode(field.config, field.key) === 'time'"
                      v-model="customer[field.key]"
                      value-format="HH:mm:ss"
                      placeholder="请选择时间"
                      style="width: 100%;"
                    ></el-time-picker>

                    <el-switch
                      v-else-if="getFieldMode(field.config, field.key) === 'boolean'"
                      v-model="customer[field.key]"
                      :active-value="true"
                      :inactive-value="false"
                    ></el-switch>

                    <el-select
                      v-else
                      v-model="customer[field.key]"
                      filterable
                      placeholder="请选择"
                    >
                      <el-option
                        v-for="item in getFieldOptions(field.config, field.key)"
                        :key="'support-opt-' + index + '-' + field.key + '-' + getOptionValue(item)"
                        :label="getOptionLabel(item)"
                        :value="getOptionValue(item)"
                      />
                    </el-select>
                  </div>

                  <div
                    v-else
                    :key="'support-country-' + index + '-' + field.key"
                    class="form-item form-item--full"
                  >
                    <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>
                    <div class="inline-field">
                      <el-select
                        :value="getCountryParent(customer, getCustomerIndex(customer))"
                        filterable
                        placeholder="选择国家/地区"
                        @input="setCountryParent(customer, getCustomerIndex(customer), $event)"
                      >
                        <el-option
                          v-for="item in getFieldOptions(field.config, field.key)"
                          :key="'support-country-parent-' + index + '-' + item.id"
                          :label="getOptionLabel(item)"
                          :value="String(item.id)"
                        />
                      </el-select>
                      <el-select
                        v-model="customer[field.key]"
                        filterable
                        placeholder="选择城市"
                      >
                        <el-option
                          v-for="city in getCountryCities(customer, getCustomerIndex(customer), field.config)"
                          :key="'support-country-city-' + index + '-' + city.city_code"
                          :label="city.name"
                          :value="city.city_code"
                        />
                      </el-select>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <section
              v-for="(traffic, index) in booking.traffic"
              :key="'traffic-' + index"
              class="order-card"
            >
              <div class="section-title">{{ getTrafficTitle(traffic.traffic_type, index) }}</div>
              <div class="form-grid">
                <div
                  v-for="field in getVisibleTrafficFields(traffic.traffic_type)"
                  :key="'traffic-field-' + index + '-' + field.key"
                  class="form-item"
                  :class="{ 'form-item--full': isWideField(field.key) }"
                >
                  <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>

                  <el-input
                    v-if="getFieldMode(field.config, field.key) === 'text'"
                    v-model.trim="traffic[field.key]"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-input
                    v-else-if="getFieldMode(field.config, field.key) === 'number'"
                    v-model.trim="traffic[field.key]"
                    type="number"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-input
                    v-else-if="getFieldMode(field.config, field.key) === 'textarea'"
                    v-model.trim="traffic[field.key]"
                    type="textarea"
                    :rows="3"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-date-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'date'"
                    v-model="traffic[field.key]"
                    type="date"
                    value-format="yyyy-MM-dd"
                    placeholder="请选择日期"
                    style="width: 100%;"
                  ></el-date-picker>

                  <el-time-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'time'"
                    v-model="traffic[field.key]"
                    value-format="HH:mm:ss"
                    placeholder="请选择时间"
                    style="width: 100%;"
                  ></el-time-picker>

                  <el-switch
                    v-else-if="getFieldMode(field.config, field.key) === 'boolean'"
                    v-model="traffic[field.key]"
                    :active-value="true"
                    :inactive-value="false"
                  ></el-switch>

                  <el-select
                    v-else
                    v-model="traffic[field.key]"
                    filterable
                    placeholder="请选择"
                  >
                    <el-option
                      v-for="item in getFieldOptions(field.config, field.key)"
                      :key="'traffic-opt-' + index + '-' + field.key + '-' + getOptionValue(item)"
                      :label="getOptionLabel(item)"
                      :value="getOptionValue(item)"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section v-if="hasMobileDeviceFields" class="order-card">
              <div class="section-title">其他设备信息</div>
              <div class="form-grid">
                <div
                  v-for="field in mobileDeviceFields"
                  :key="'mobile-' + field.key"
                  class="form-item"
                  :class="{ 'form-item--full': isWideField(field.key) }"
                >
                  <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>

                  <el-input
                    v-if="getFieldMode(field.config, field.key) === 'text'"
                    v-model.trim="booking.mobile_device[field.key]"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-date-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'date'"
                    v-model="booking.mobile_device[field.key]"
                    type="date"
                    value-format="yyyy-MM-dd"
                    placeholder="请选择日期"
                    style="width: 100%;"
                  ></el-date-picker>

                  <el-time-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'time'"
                    v-model="booking.mobile_device[field.key]"
                    value-format="HH:mm:ss"
                    placeholder="请选择时间"
                    style="width: 100%;"
                  ></el-time-picker>

                  <el-switch
                    v-else-if="getFieldMode(field.config, field.key) === 'boolean'"
                    v-model="booking.mobile_device[field.key]"
                    :active-value="true"
                    :inactive-value="false"
                  ></el-switch>

                  <el-input
                    v-else-if="getFieldMode(field.config, field.key) === 'textarea'"
                    v-model.trim="booking.mobile_device[field.key]"
                    type="textarea"
                    :rows="3"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-select
                    v-else
                    v-model="booking.mobile_device[field.key]"
                    filterable
                    placeholder="请选择"
                  >
                    <el-option
                      v-for="item in getFieldOptions(field.config, field.key)"
                      :key="'mobile-opt-' + field.key + '-' + getOptionValue(item)"
                      :label="getOptionLabel(item)"
                      :value="getOptionValue(item)"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section class="order-card">
              <div class="section-title">订单备注</div>
              <el-input
                v-model.trim="booking.order_note"
                type="textarea"
                :rows="4"
                maxlength="900"
                show-word-limit
                placeholder="请输入备注信息，可留空"
              ></el-input>
            </section>
          </div>

          <aside class="order-side">
            <div class="side-card">
              <div class="side-title">订单总览</div>
              <div class="side-price">¥ {{ formatMoney(orderTotalPrice) }}</div>
              <div class="side-list">
                <div class="side-row">
                  <span>商品</span>
                  <strong>{{ productTitle }}</strong>
                </div>
                <div class="side-row">
                  <span>套餐</span>
                  <strong>{{ packageTitle }}</strong>
                </div>
                <div class="side-row">
                  <span>数量</span>
                  <strong>{{ totalQuantity }} {{ orderUnit || '份' }}</strong>
                </div>
                <div v-if="shouldShowServiceDate()" class="side-row">
                  <span>日期</span>
                  <strong>{{ booking.s_date || '-' }}</strong>
                </div>
              </div>
              <div v-if="refundPolicyLines.length" class="side-note">
                {{ refundPolicyLines[0] }}
              </div>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="submitting"
                @click="submitOrder"
              >
                {{ submitting ? '提交中...' : '提交预订' }}
              </el-button>
            </div>
          </aside>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-order',
  components: {
    nav_list,
  },
  data() {
    return {
      loading: true,
      submitting: false,
      requestData: null,
      bookingField: {
        traffics: [],
        custom: {},
      },
      booking: {
        custom: [],
        traffic: [],
        mobile_device: {},
        buyer_first_name: '',
        buyer_last_name: '',
        buyer_country: 'CN',
        buyer_tel_country_code: '86',
        buyer_tel_number: '',
        buyer_Email: '',
        partner_order_no: '',
        guide_lang: '',
        order_note: '',
        pay_type: '01',
        s_date: '',
      },
      prodInfo: null,
      refundPolicy: null,
      countryAreas: [
        { country_code: 'CN', country_name: '中国', tel_area: '86' },
        { country_code: 'JP', country_name: '日本', tel_area: '81' },
        { country_code: 'US', country_name: '美国', tel_area: '1' },
        { country_code: 'HK', country_name: '中国香港', tel_area: '852' },
      ],
      countryParentMap: {},
    };
  },
  computed: {
    productTitle() {
      return this.prodInfo && this.prodInfo.prod_name
        ? this.prodInfo.prod_name
        : this.requestData && this.requestData.extra && this.requestData.extra.prod_name
          ? this.requestData.extra.prod_name
          : '商品预订';
    },
    packageTitle() {
      return this.prodInfo && this.prodInfo.pkg_name
        ? this.prodInfo.pkg_name
        : this.requestData && this.requestData.extra && this.requestData.extra.pkg_name
          ? this.requestData.extra.pkg_name
          : '套餐信息';
    },
    orderTotalPrice() {
      return this.requestData && this.requestData.b2c_total_price
        ? Number(this.requestData.b2c_total_price)
        : 0;
    },
    totalQuantity() {
      if (!this.booking.skus || !this.booking.skus.length) return 0;
      return this.booking.skus.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    },
    orderUnit() {
      return this.requestData && this.requestData.extra ? this.requestData.extra.unit || '' : '';
    },
    selectedSpecTexts() {
      const specs = this.requestData && this.requestData.extra ? this.requestData.extra.specs || [] : [];
      const specRefs = this.getSelectedSpecRefs();
      return specs.map((spec) => {
        const selected = (spec.spec_items || []).filter((item) => specRefs.indexOf(item.spec_item_oid) !== -1);
        if (!selected.length) return '';
        return spec.spec_title + '：' + selected.map((item) => item.name).join(' / ');
      }).filter(Boolean);
    },
    showGuideLang() {
      return this.bookingField
        && this.bookingField.guide_lang
        && this.isRequired(this.bookingField.guide_lang);
    },
    travelerCustomers() {
      return (this.booking.custom || []).filter((item) => item.cus_type === 'cus_01' || item.cus_type === 'cus_02');
    },
    supportCustomers() {
      return (this.booking.custom || []).filter((item) => item.cus_type !== 'cus_01' && item.cus_type !== 'cus_02');
    },
    hasMobileDeviceFields() {
      return this.mobileDeviceFields.length > 0;
    },
    mobileDeviceFields() {
      const fields = [];
      const config = this.bookingField && this.bookingField.mobile_device ? this.bookingField.mobile_device : {};
      Object.keys(config).forEach((key) => {
        if (config[key] && typeof config[key] === 'object') {
          fields.push({ key, config: config[key] });
        }
      });
      return fields;
    },
    refundPolicyLines() {
      const props = this.getPmdlProperties(this.effectiveRefundPolicy);
      if (!props) return [];
      if (props.partial_refund && props.partial_refund.list && props.partial_refund.list.length) {
        return props.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      if (props.policy_type && props.policy_type.desc) {
        return [props.policy_type.desc];
      }
      return [];
    },
    effectiveRefundPolicy() {
      if (this.requestData && this.requestData.extra && this.requestData.extra.pkg_refund_policy) {
        return this.requestData.extra.pkg_refund_policy;
      }
      return this.refundPolicy;
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    async initPage() {
      this.loading = true;
      try {
        const cached = this.cache('kkday_order_req');
        if (!cached) {
          this.requestData = null;
          return;
        }
        this.requestData = cached;
        await this.loadBookingData();
      } catch (error) {
        this.$message.error('加载订单页失败');
      } finally {
        this.loading = false;
      }
    },
    async loadBookingData() {
      const res = await this.get('/kkday/bookingf/load-data', this.requestData, 'post', false);
      this.bookingField = res && res.bookingfield ? res.bookingfield : { traffics: [], custom: {} };
      this.prodInfo = res && res.prod_info ? res.prod_info : null;
      this.refundPolicy = res && res.pmdl_refund_policy ? res.pmdl_refund_policy : null;

      const bookingRes = res && res.booking ? res.booking : {};
      this.booking = {
        custom: this.buildCustomData(bookingRes, this.bookingField),
        traffic: this.buildTrafficData(bookingRes, this.bookingField, this.requestData),
        mobile_device: this.buildMobileDeviceData(bookingRes, this.bookingField),
        buyer_first_name: bookingRes.buyer_first_name || '',
        buyer_last_name: bookingRes.buyer_last_name || '',
        buyer_country: bookingRes.buyer_country || 'CN',
        buyer_tel_country_code: '86',
        buyer_tel_number: bookingRes.buyer_tel_number || '',
        buyer_Email: bookingRes.buyer_Email || '',
        partner_order_no: bookingRes.partner_order_no || '',
        guide_lang: bookingRes.guide_lang || '',
        order_note: bookingRes.order_note || '',
        pay_type: bookingRes.pay_type || '01',
        s_date: bookingRes.s_date || this.requestData.s_date || '',
        e_date: bookingRes.e_date || this.requestData.e_date || '',
        skus: bookingRes.skus || this.requestData.skus || [],
      };

      this.initCountryParents();
    },
    buildCustomData(bookingRes, bookingField) {
      const list = [];
      const resultCustom = bookingRes && bookingRes.custom ? bookingRes.custom : [];
      const config = bookingField && bookingField.custom ? bookingField.custom : {};

      if (resultCustom.length) {
        resultCustom.forEach((item) => {
          const customer = { cus_type: item.cus_type };
          Object.keys(config).forEach((key) => {
            if (key === 'cus_type' || !config[key] || typeof config[key] !== 'object') return;
          if (typeof item[key] !== 'undefined') {
              customer[key] = key === 'tel_country_code' ? '86' : item[key];
            } else {
              customer[key] = key === 'tel_country_code' ? '86' : this.getEmptyValue(config[key]);
            }
          });
          list.push(customer);
        });
      } else if (config.cus_type && config.cus_type.use && config.cus_type.use.length) {
        config.cus_type.use.forEach((cusType) => {
          const customer = { cus_type: cusType };
          Object.keys(config).forEach((key) => {
            if (key === 'cus_type' || !config[key] || typeof config[key] !== 'object') return;
            customer[key] = key === 'tel_country_code' ? '86' : this.getEmptyValue(config[key]);
          });
          list.push(customer);
        });
      }

      return list;
    },
    buildTrafficData(bookingRes, bookingField, requestData) {
      const list = [];
      const trafficConfigList = bookingField && bookingField.traffics ? bookingField.traffics : [];
      const resultTraffic = bookingRes && bookingRes.traffic ? bookingRes.traffic : [];

      trafficConfigList.forEach((config) => {
        const trafficType = config.traffic_type && config.traffic_type.traffic_type_value
          ? config.traffic_type.traffic_type_value
          : '';
        if (!trafficType) return;

        const existing = resultTraffic.find((item) => item.traffic_type === trafficType) || {};
        const traffic = {
          traffic_type: trafficType,
        };

        Object.keys(config).forEach((key) => {
          if (key === 'traffic_type' || !config[key] || typeof config[key] !== 'object') return;
          if (typeof existing[key] !== 'undefined' && existing[key] !== null && existing[key] !== '') {
            traffic[key] = existing[key];
          } else if (config[key].type === 'date') {
            traffic[key] = bookingRes && bookingRes.s_date ? bookingRes.s_date : requestData.s_date || '';
          } else if (trafficType === 'psg_qty') {
            traffic[key] = '0';
          } else {
            traffic[key] = this.getEmptyValue(config[key]);
          }
        });

        list.push(traffic);
      });

      return list;
    },
    buildMobileDeviceData(bookingRes, bookingField) {
      const result = bookingRes && bookingRes.mobile_device ? bookingRes.mobile_device : {};
      const config = bookingField && bookingField.mobile_device ? bookingField.mobile_device : {};
      const data = {};

      Object.keys(config).forEach((key) => {
        if (!config[key] || typeof config[key] !== 'object') return;
        data[key] = typeof result[key] !== 'undefined' ? result[key] : this.getEmptyValue(config[key]);
      });

      return data;
    },
    getEmptyValue(config) {
      if (!config) return '';
      if (config.type === 'boolean') return null;
      return '';
    },
    initCountryParents() {
      this.countryParentMap = {};
      (this.booking.custom || []).forEach((item, index) => {
        if (item.country_cities && String(item.country_cities).indexOf('_') !== -1) {
          this.$set(this.countryParentMap, String(index), String(item.country_cities).split('_')[0]);
        }
      });
    },
    getSelectedSpecRefs() {
      if (!this.requestData || !this.requestData.skus) return [];
      return this.requestData.skus.reduce((result, item) => {
        return result.concat(item.specs_ref || []);
      }, []);
    },
    getPmdlProperties(moduleData) {
      if (!moduleData) return null;
      if (moduleData.content && moduleData.content.properties) return moduleData.content.properties;
      if (moduleData.properties) return moduleData.properties;
      return moduleData;
    },
    getVisibleCustomFields(cusType) {
      const fields = [];
      const config = this.bookingField && this.bookingField.custom ? this.bookingField.custom : {};
      Object.keys(config).forEach((key) => {
        if (key === 'cus_type' || !config[key] || typeof config[key] !== 'object') return;
        if (key === 'tel_country_code') return;
        if (this.isFieldVisible(config[key], cusType)) {
          fields.push({ key, config: config[key] });
        }
      });
      return fields;
    },
    getVisibleTrafficFields(trafficType) {
      const config = (this.bookingField.traffics || []).find((item) => {
        return item.traffic_type && item.traffic_type.traffic_type_value === trafficType;
      });
      if (!config) return [];
      return Object.keys(config).filter((key) => key !== 'traffic_type' && config[key] && typeof config[key] === 'object')
        .map((key) => ({ key, config: config[key] }))
        .filter((field) => this.isRequired(field.config) || field.config.list_option || field.config.type);
    },
    isFieldVisible(config, cusType) {
      if (!config) return false;
      const use = config.use && Array.isArray(config.use) ? config.use.slice() : null;
      if (use && use.length) {
        const types = String(cusType).split(',').map((item) => item.trim());
        if (types.indexOf('cus_01') !== -1 && types.indexOf('cus_02') === -1) {
          types.push('cus_02');
        }
        return use.some((item) => types.indexOf(item) !== -1);
      }
      return this.isRequired(config);
    },
    isRequired(config) {
      if (!config) return false;
      if (typeof config.is_require === 'string') {
        return config.is_require === 'True' || config.is_require === 'true';
      }
      return !!config.is_require;
    },
    showBuyerField(fieldKey) {
      const pathMap = {
        first_name: 'booking.first_name',
        last_name: 'booking.last_name',
        tel: 'booking.tel',
        email: 'booking.email',
      };
      const config = this.getValue(this.bookingField, pathMap[fieldKey]);
      if (!config) return true;
      return this.isFieldVisible(config, 'cus_01');
    },
    getValue(obj, path) {
      if (!obj || !path) return undefined;
      return path.split('.').reduce((result, key) => (result ? result[key] : undefined), obj);
    },
    getFieldLabel(key, config) {
      const map = {
        native_first_name: '本地名',
        native_last_name: '本地姓',
        english_first_name: '英文名',
        english_last_name: '英文姓',
        birth: '出生日期',
        passport_no: '护照号码',
        passport_expdate: '护照有效期',
        nationality: '国籍',
        id_no: '身份证号',
        mtp_no: 'MTP 号码',
        gender: '性别',
        tel_country_code: '区号',
        tel_number: '联系电话',
        has_app: '是否有通讯软件',
        contact_app: '通讯软件',
        contact_app_account: '通讯账号',
        country_cities: '国家/城市',
        zipcode: '邮编',
        address: '详细地址',
        hotel_name: '酒店名称',
        hotel_tel_number: '酒店电话',
        booking_website: '预订网站',
        booking_order_no: '预订单号',
        check_in_date: '入住日期',
        check_out_date: '退房日期',
        height: '身高',
        height_unit: '身高单位',
        weight: '体重',
        weight_unit: '体重单位',
        shoe_type: '鞋型',
        shoe: '鞋码',
        shoe_unit: '鞋码单位',
        glass_degree: '眼镜度数',
        meal: '餐食偏好',
        mobile_model_no: '设备型号',
        IMEI: 'IMEI 号码',
        active_date: '激活日期',
        s_date: '开始日期',
        e_date: '结束日期',
        s_time: '开始时间',
        e_time: '结束时间',
        s_location: '出发地点',
        e_location: '结束地点',
        departure_airport: '出发机场',
        arrival_airport: '到达机场',
        departure_terminalNo: '出发航站楼',
        arrival_terminalNo: '到达航站楼',
        departure_airlineName: '出发航空公司',
        arrival_airlineName: '到达航空公司',
        departure_flightNo: '出发航班号',
        arrival_flightNo: '到达航班号',
        departure_date: '出发日期',
        arrival_date: '到达日期',
        departure_time: '出发时间',
        arrival_time: '到达时间',
        departure_flightType: '出发航班类型',
        arrival_flightType: '到达航班类型',
      };
      return config && (config.title || config.name || config.desc)
        ? config.title || config.name || config.desc
        : (map[key] || key);
    },
    getFieldMode(config, key) {
      if (key === 'country_cities') return 'country_cities';
      if (key === 'address' || key === 'order_note') return 'textarea';
      if (this.getFieldOptions(config, key).length) return 'select';
      if (config && config.type === 'date') return 'date';
      if (config && config.type === 'time') return 'time';
      if (config && config.type === 'boolean') return 'boolean';
      if (config && typeof config.type === 'string' && ['int', 'integer', 'number'].indexOf(config.type.toLowerCase()) !== -1) {
        return 'number';
      }
      if (key === 'height' || key === 'weight') return 'number';
      return 'text';
    },
    getFieldOptions(config, key) {
      if (!config) return [];
      if (config.list_option && config.list_option.length) return config.list_option;
      if (config.location && config.location.length) return config.location;
      if (key === 'tel_country_code' || key === 'buyer_tel_country_code') {
        return this.countryAreas.map((item) => ({
          id: item.tel_area,
          code: item.tel_area,
          name: '+' + item.tel_area + ' ' + item.country_name,
        }));
      }
      return [];
    },
    getOptionLabel(item) {
      if (!item) return '';
      return item.name || item.label || item.title || item.info || item.code || item.id || '';
    },
    getOptionValue(item) {
      if (!item) return '';
      return item.id || item.code || item.value || item.name || '';
    },
    isWideField(key) {
      return ['address', 'order_note', 'country_cities'].indexOf(key) !== -1;
    },
    getCountryParent(customer, index) {
      return this.countryParentMap[String(index)] || '';
    },
    getCustomerIndex(customer) {
      return this.booking.custom.indexOf(customer);
    },
    setCountryParent(customer, index, value) {
      this.$set(this.countryParentMap, String(index), value);
      customer.country_cities = '';
    },
    getCountryCities(customer, index, config) {
      const parent = this.getCountryParent(customer, index);
      const country = (this.getFieldOptions(config, 'country_cities') || []).find((item) => String(item.id) === String(parent));
      if (!country || !country.cities) return [];
      return country.cities.map((city) => ({
        name: city.name,
        city_code: String(country.id) + '_' + city.city_code,
      }));
    },
    getCustomerTitle(customer, index, type) {
      if (customer.cus_type === 'cus_01') return '旅客代表';
      if (customer.cus_type === 'cus_02') {
        const total = this.travelerCustomers.filter((item) => item.cus_type === 'cus_02').length;
        return total <= 1 ? '主旅客' : '旅客 ' + (index + 1);
      }
      const map = {
        contact: '旅游期间联系方式',
        send: '寄送资料',
      };
      return map[customer.cus_type] || (type === 'support' ? '补充信息' : '旅客信息');
    },
    getTrafficTitle(trafficType, index) {
      const map = {
        Flight: '航班信息',
        flight: '航班信息',
        rentcar_01: '租车服务',
        rentcar_02: '租车服务',
        rentcar_03: '租车服务',
        pickup_01: '接送信息',
        pickup_02: '接送信息',
        pickup_03: '接送信息',
        pickup_04: '接送信息',
        voucher: '兑换信息',
        psg_qty: '人数配置',
      };
      return map[trafficType] || ('服务信息 ' + (index + 1));
    },
    shouldShowServiceDate() {
      if (this.requestData && this.requestData.extra && this.requestData.extra.go_date_setting
        && this.requestData.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.prodInfo && this.prodInfo.go_date_setting && this.prodInfo.go_date_setting.type === '04') {
        return false;
      }
      return !!this.booking.s_date;
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    buildSubmitPayload() {
      const orderData = {
        guid: this.requestData.guid,
        prod_no: this.requestData.prod_no,
        pkg_no: this.requestData.pkg_no,
        item_no: this.requestData.item_no,
        s_date: this.requestData.s_date,
        e_date: this.booking.e_date || this.requestData.e_date || this.requestData.s_date,
        event_time: this.requestData.event_time,
        skus: this.requestData.skus,
        total_price: this.requestData.total_price,
        b2c_total_price: this.requestData.b2c_total_price,
        locale: 'zh-cn',
        state: 'CN',
        buyer_tel_country_code: '86',
        buyer_tel_number: this.booking.buyer_tel_number,
        buyer_country: this.booking.buyer_country || 'CN',
        buyer_first_name: this.booking.buyer_first_name,
        buyer_last_name: this.booking.buyer_last_name,
        buyer_Email: this.booking.buyer_Email,
        order_note: this.booking.order_note,
        guide_lang: this.booking.guide_lang,
        pay_type: this.booking.pay_type || '01',
        custom: (this.booking.custom || []).map((item) => this.filterCustomerData(item)),
        traffic: (this.booking.traffic || []).map((item) => this.filterTrafficData(item)),
        mobile_device: this.filterMobileDeviceData(),
      };

      return {
        order_data: orderData,
        extra: this.requestData.extra || {},
      };
    },
    filterCustomerData(item) {
      const result = {
        cus_type: item.cus_type,
      };
      this.getVisibleCustomFields(item.cus_type).forEach((field) => {
        result[field.key] = item[field.key];
      });
      return result;
    },
    filterTrafficData(item) {
      const result = {
        traffic_type: item.traffic_type,
      };
      this.getVisibleTrafficFields(item.traffic_type).forEach((field) => {
        result[field.key] = item[field.key];
      });
      return result;
    },
    filterMobileDeviceData() {
      const result = {};
      this.mobileDeviceFields.forEach((field) => {
        result[field.key] = this.booking.mobile_device[field.key];
      });
      return result;
    },
    validateEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    isEmptyValue(value) {
      return value === '' || value === null || typeof value === 'undefined';
    },
    validateBooking() {
      this.booking.buyer_tel_country_code = '86';
      (this.booking.custom || []).forEach((item) => {
        if (Object.prototype.hasOwnProperty.call(item, 'tel_country_code')) {
          this.$set(item, 'tel_country_code', '86');
        }
      });
      if (this.showBuyerField('first_name') && !this.booking.buyer_first_name) {
        return '请填写买家名';
      }
      if (this.showBuyerField('last_name') && !this.booking.buyer_last_name) {
        return '请填写买家姓';
      }
      if (this.showBuyerField('tel') && !this.booking.buyer_tel_number) {
        return '请填写联系电话';
      }
      if (this.showBuyerField('email')) {
        if (!this.booking.buyer_Email) {
          return '请填写邮箱';
        }
        if (!this.validateEmail(this.booking.buyer_Email)) {
          return '请输入有效的邮箱';
        }
      }
      if (this.showGuideLang && !this.booking.guide_lang) {
        return '请选择导游语言';
      }

      for (let i = 0; i < this.booking.custom.length; i += 1) {
        const customer = this.booking.custom[i];
        const fields = this.getVisibleCustomFields(customer.cus_type);
        for (let j = 0; j < fields.length; j += 1) {
          const field = fields[j];
          if (!this.isRequired(field.config)) continue;
          if (this.isEmptyValue(customer[field.key])) {
            return this.getCustomerTitle(customer, i, 'custom') + '：请填写' + this.getFieldLabel(field.key, field.config);
          }
        }
      }

      for (let i = 0; i < this.booking.traffic.length; i += 1) {
        const traffic = this.booking.traffic[i];
        const fields = this.getVisibleTrafficFields(traffic.traffic_type);
        for (let j = 0; j < fields.length; j += 1) {
          const field = fields[j];
          if (!this.isRequired(field.config)) continue;
          if (this.isEmptyValue(traffic[field.key])) {
            return this.getTrafficTitle(traffic.traffic_type, i) + '：请填写' + this.getFieldLabel(field.key, field.config);
          }
        }
      }

      for (let i = 0; i < this.mobileDeviceFields.length; i += 1) {
        const field = this.mobileDeviceFields[i];
        if (this.isEmptyValue(this.booking.mobile_device[field.key])) {
          return '请填写' + this.getFieldLabel(field.key, field.config);
        }
      }

      return '';
    },
    async submitOrder() {
      const errorMessage = this.validateBooking();
      if (errorMessage) {
        this.$message.warning(errorMessage);
        return;
      }

      this.submitting = true;
      try {
        const payload = this.buildSubmitPayload();
        const res = await this.get('/kkday/test_create', payload, 'post', false);
        if (res && res.sn) {
          this.cache('kkday_last_order_sn', res.sn);
          this.jump('/shop/pay', {
            sn: res.sn,
            from: 'checkout',
          });
        } else {
          this.$message.success('订单创建成功');
        }
      } catch (error) {
        this.$message.error('订单创建失败，请重试');
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.back();
      } else {
        this.jump('/shop/info', {
          prod_no: this.requestData ? this.requestData.prod_no : '',
          id: this.requestData ? this.requestData.prod_no : '',
        });
      }
    },
  },
};
</script>

<style scoped>
.shop-order-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.order-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

.state-card,
.order-card,
.side-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.state-card {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.state-card--empty {
  text-align: center;
}

.state-title {
  color: #223046;
  font-size: 24px;
  font-weight: bold;
}

.state-desc {
  margin: 12px 0 24px;
  color: #7b8798;
  font-size: 14px;
}

.breadcrumb-row {
  margin-bottom: 16px;
}

.order-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.order-main {
  flex: 1;
  min-width: 0;
}

.order-side {
  width: 300px;
  flex-shrink: 0;
}

.order-card {
  margin-bottom: 20px;
  padding: 24px;
}

.hero-card {
  padding: 28px;
  background: linear-gradient(135deg, #fbfdff 0%, #f1f6ff 100%);
}

.hero-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.hero-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eaf2ff;
  color: #1c5098;
  font-size: 12px;
}

.hero-title {
  margin: 14px 0 0;
  color: #223046;
  font-size: 30px;
  line-height: 1.4;
}

.hero-subtitle {
  margin-top: 10px;
  color: #718197;
  font-size: 15px;
}

.hero-price {
  flex-shrink: 0;
  color: #1c5098;
  font-weight: bold;
}

.price-prefix {
  font-size: 20px;
}

.price-value {
  font-size: 42px;
  line-height: 1;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.meta-chip {
  min-width: 180px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 10px;
}

.meta-label {
  display: block;
  color: #7b8798;
  font-size: 12px;
}

.meta-value {
  display: block;
  margin-top: 8px;
  color: #223046;
  font-size: 15px;
  font-weight: bold;
}

.selected-specs {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e4ebf5;
}

.inline-title,
.section-title {
  color: #223046;
  font-size: 20px;
  font-weight: bold;
}

.spec-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.spec-tag {
  padding: 6px 12px;
  border-radius: 999px;
  background: #edf4ff;
  color: #1c5098;
  font-size: 13px;
}

.policy-list {
  margin: 16px 0 0;
  padding-left: 20px;
  color: #5f6c80;
  line-height: 1.9;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
  margin-top: 18px;
}

.form-item {
  min-width: 0;
}

.form-item--full {
  grid-column: 1 / -1;
}

.form-item label {
  display: block;
  margin-bottom: 10px;
  color: #314155;
  font-size: 14px;
  font-weight: bold;
}

.required {
  margin-left: 4px;
  color: #dd4c38;
}

.inline-field {
  display: flex;
  gap: 12px;
}

.side-card {
  position: sticky;
  top: 84px;
  padding: 22px;
}

.side-title {
  color: #223046;
  font-size: 18px;
  font-weight: bold;
}

.side-price {
  margin-top: 18px;
  color: #1c5098;
  font-size: 34px;
  font-weight: bold;
}

.side-list {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e6edf7;
}

.side-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  color: #6a778b;
  font-size: 13px;
}

.side-row strong {
  max-width: 160px;
  color: #223046;
  font-weight: bold;
  text-align: right;
}

.side-note {
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  color: #6a778b;
  font-size: 12px;
  line-height: 1.8;
}

.submit-btn {
  width: 100%;
  margin-top: 18px;
}

@media (max-width: 1260px) {
  .order-wrap {
    width: calc(100% - 24px);
  }
}

@media (max-width: 992px) {
  .order-layout {
    display: block;
  }

  .order-side {
    width: 100%;
  }

  .side-card {
    position: static;
    margin-top: 20px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .hero-head,
  .inline-field {
    display: block;
  }

  .hero-price {
    margin-top: 16px;
  }

}
</style>



// WEBPACK FOOTER //
// src/pages/shop/order.vue